import {
	apiServices,
	createUserVisibleError,
	getApiErrorMessage,
} from "../../../../core/utilities/ApiServices";
import { Likes } from "../../domain/entities/userMatch";
import { LikesParams } from "../../domain/entities/getLikesParams";
import { MatchesSearch } from "../../domain/entities/userUnjabbed";
import { MatchSearchParams } from "../../domain/entities/getMatchSearchParams";
import { ReportSubject } from "../../domain/entities/getReportSubject";
import { UserInfoProfile } from "../../domain/entities/userInfoProfile";
import { DeleteBlockMatchParams } from "../../domain/entities/deleteBlockMatchParams";
import { LikesYouResponse } from "../../domain/entities/likesYou";

export interface MatchDataSource {
	getMatchSearch(params: MatchSearchParams): Promise<MatchesSearch>;
	getLikes(params: LikesParams): Promise<LikesYouResponse>;
	getMyLikes(params: LikesParams): Promise<Likes[]>;
	getMyMatches(params: LikesParams): Promise<Likes[]>;
	getMyNewMatches(params: LikesParams): Promise<Likes[]>;
	deleteLike(match_id: string): Promise<void>;
	rejectMatch(match_id: string): Promise<void>;
	getReportSubject(): Promise<ReportSubject[]>;
	createOrderPaypal(product_id: string, quantity: number): Promise<string>;
	sendPaidMessage(user_id: string, message: string): Promise<void>;
	getUserInfoById(params: {
		user_id: string;
		user_lat: string;
		user_lng: string;
		distance_origin_lat?: string;
		distance_origin_lng?: string;
	}): Promise<UserInfoProfile>;
	deleteTrashUser(user_id: string): Promise<void>;
	deleteBlockMatch(params: DeleteBlockMatchParams): Promise<void>;
}

export class MatchDataSourceImpl implements MatchDataSource {
	async getMatchSearch(params: MatchSearchParams): Promise<MatchesSearch> {
		const {
			second_look: secondLook,
			second_look_snapshot: secondLookSnapshot,
			...searchParams
		} = params;
		const response = await apiServices.get(
			secondLook ? `/match/second-look` : `/match/search`,
			{
				...(secondLook
					? {}
					: {
							headers: {
								"Cache-Control": "no-cache",
								Pragma: "no-cache",
							},
						}),
				params: secondLook
					? {
						...searchParams,
						...(secondLookSnapshot
							? { second_look_snapshot: secondLookSnapshot }
							: {}),
					}
					: searchParams,
			},
		);
		if (response.status === 200) {
			if (secondLook) {
				const snapshot = response.data?.second_look_snapshot;
				const matches = response.data?.matches;
				const hasValidSnapshot =
					typeof snapshot === "string" &&
					snapshot.trim().length > 0 &&
					Number.isFinite(Date.parse(snapshot));
				const hasValidMatches =
					Array.isArray(matches) &&
					matches.every(
						(match: unknown) =>
							typeof match === "object" &&
							match !== null &&
							typeof (match as { id?: unknown }).id === "string" &&
							(match as { id: string }).id.trim().length > 0,
					);
				const hasValidPage =
					Number.isInteger(response.data?.page) && response.data.page >= 1;
				if (!hasValidSnapshot || !hasValidMatches || !hasValidPage) {
					throw new Error("Malformed Second Look response");
				}
			}
			return response.data;
		} else {
			throw new Error();
		}
	}
	async getLikes(params: LikesParams): Promise<LikesYouResponse> {
		const response = await apiServices.get(`/match/likes-you`, {
			params,
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async getMyLikes(params: LikesParams): Promise<Likes[]> {
		const response = await apiServices.get(`/match/my-likes`, {
			params,
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async getMyMatches(params: LikesParams): Promise<Likes[]> {
		const response = await apiServices.get(`/match/my-matches`, {
			params,
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async getMyNewMatches(params: LikesParams): Promise<Likes[]> {
		const response = await apiServices.get(`/match/my-new-matches`, {
			params,
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async getUserInfoById(params: {
		user_id: string;
		user_lat: string;
		user_lng: string;
		distance_origin_lat?: string;
		distance_origin_lng?: string;
	}): Promise<UserInfoProfile> {
		const response = await apiServices.get(`/match/user-info-by-id`, {
			params,
		});
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async deleteLike(match_id: string): Promise<void> {
		const response = await apiServices.delete(`/match/delete-like`, {
			params: { match_id },
		});
		if (response.status === 200) {
			return;
		} else {
			throw new Error();
		}
	}
	async rejectMatch(match_id: string): Promise<void> {
		const response = await apiServices.delete(`/match/reject-match`, {
			params: { match_id },
		});
		if (response.status === 200) {
			return;
		} else {
			throw new Error();
		}
	}
	async getReportSubject(): Promise<ReportSubject[]> {
		const response = await apiServices.get(`/match/report-subject`);
		if (response.status === 200) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async createOrderPaypal(product_id: string, quantity: number): Promise<string> {
		const params = {
			product_id,
			quantity,
		};
		const response = await apiServices.post(`/match/create-order`, params);
		if (response.status === 200 || response.status === 201) {
			return response.data;
		} else {
			throw new Error();
		}
	}
	async sendPaidMessage(user_id: string, message: string): Promise<void> {
		const params = {
			user_id,
			message,
		};
		let response;
		try {
			response = await apiServices.post(`/match/paid-message`, params);
		} catch (error) {
			throw createUserVisibleError(getApiErrorMessage(error));
		}
		if (response.status === 200 || response.status === 201) {
			return;
		} else {
			throw new Error();
		}
	}
	async deleteTrashUser(user_id: string): Promise<void> {
		const response = await apiServices.delete(`/match/delete-trash-user`, {
			params: { user_id },
		});
		if (response.status === 200) {
			return;
		} else {
			throw new Error();
		}
	}
	async deleteBlockMatch(params: DeleteBlockMatchParams): Promise<void> {
		const response = await apiServices.delete(`/match/block-match`, { params });
		if (response.status === 200) {
			return;
		} else {
			throw new Error();
		}
	}
}
